import { ChatAltIcon } from '@heroicons/react/outline'
import { User } from '@prisma/client'
import Button from 'components/Button'
import ProfileImageWithFallback from 'components/ProfileImageWithFallback'
import { FormEvent } from 'react'
import styles from './TweetInput.module.css'

type Props = {
  user?: User,
  text: string,
  setText: (text: string) => void,
  handleSubmit: (event: FormEvent<HTMLFormElement> | MouseEvent) => void,
  placeholder: string,
  buttonLabel: string | React.ReactNode,
  buttonLabelWorking?: string,
  isDisabled?: boolean,
  isWorking?: boolean,
  buttonIsPrimary?: boolean,
  suppressPrimaryButton?: boolean,
  additionalButtons?: React.ReactNode,
  suppressUserImage?: boolean,
}

const TweetInput = ({
  user,
  text,
  setText,
  handleSubmit,
  placeholder,
  buttonLabel,
  buttonLabelWorking,
  isDisabled = false,
  isWorking = false,
  buttonIsPrimary = true,
  additionalButtons,
  suppressPrimaryButton = false,
  suppressUserImage = false,
}: Props) => {
  return <div className={styles.tweetInput}>
      { !suppressUserImage && <div className='flex-shrink-0'>
        <div className={styles.profileImage}>
          { user?.image
            ? <ProfileImageWithFallback
              className='inline-block h-11 w-11 rounded-full'
              src={user.image}
              alt='You'
            />
            : <ChatAltIcon width={44} height={44} color='white' className={styles.profileImageIcon} />
          }
        </div>
      </div> }
      <div className='min-w-0 flex-1'>
        <form onSubmit={handleSubmit} className='relative'>
          <div className='relative border border-gray-300 rounded-lg shadow-sm overflow-hidden focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500'>
            <label htmlFor='comment' className='sr-only'>
              { placeholder }
            </label>
            <textarea
              name='comment'
              id='comment'
              className='absolute block w-full pt-2 pb-0 border-0 resize-none focus:ring-0 sm:text-sm min-h-[20px] top-0 bottom-0'
              placeholder={`${placeholder}…`}
              value={text}
              onChange={(event) => { setText(event.target.value) }}
            />
            <div className='z-50 sm:text-sm pt-3 border-0 min-h-[20px] pl-3 pr-3 pointer-events-none touch-none text-red-500 break-words min-h-[36px]'><span className='whitespace-pre-wrap'>{text}</span></div>

            {/* Spacer element to match the height of the toolbar */}
            <div className='py-2' aria-hidden='true'>
              {/* Matches height of button in toolbar (1px border + 36px content height) */}
              <div className='py-px'>
                <div className='h-9' />
              </div>
            </div>
          </div>

          <div className='absolute bottom-0 inset-x-0 pr-2 py-2 flex justify-between pointer-events-none touch-none'>{/* pl-3  */}
            <div className='flex items-center space-x-5 pl-3'>
              <div className={`flex gap-1 gap-y-0 text-xs ${ getCharacterCountColor(text.length) } flex-wrap`}>
                <div>{ text.length || 0 }</div>
                <div>of</div>
                <div>280</div>
              </div>
            </div>
            <div className='flex-shrink-0 pointer-events-auto touch-auto flex gap-3 items-center'>
              { additionalButtons }
              { !suppressPrimaryButton && <Button
                type='submit'
                className='text-white focus:outline-none focus:ring-2 focus:ring-offset-2'
                isDisabled={isDisabled}
                isWorking={isWorking}
                isWorkingLabel={buttonLabelWorking}
                isSecondary={!buttonIsPrimary}
              >
                { buttonLabel }
              </Button> }
            </div>
          </div>
        </form>
      </div>
  </div>
}

const getCharacterCountColor = (characterCount: number) => {
  if (characterCount === 0) return 'text-transparent'
  if (characterCount < 60) return 'text-gray-500'
  if (characterCount < 180) return 'text-green-500'
  if (characterCount < 280) return 'text-blue-500'
  return 'text-red-500'
}

export default TweetInput
